import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changePassword } from '../../redux/accountSlice';
import { Info } from 'lucide-react';

const SecurityTab = ({ token }) => {
  const dispatch = useDispatch();
  const { profile, loading } = useSelector((state) => state.account);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [validationError, setValidationError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');
    setSuccessMessage('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setValidationError('All fields are required.');
      return;
    }

    if (newPassword.length < 6) {
      setValidationError('New password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setValidationError('New passwords do not match.');
      return;
    }

    try {
      await dispatch(changePassword({ currentPassword, newPassword, token })).unwrap();
      setSuccessMessage('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      // Clear success notification after 4 seconds
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err) {
      setValidationError(err || 'Failed to update password.');
    }
  };

  const isGoogleAccount = profile?.authProvider === 'google';

  return (
    <div style={{ maxWidth: '650px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      
      {/* Title */}
      <h2 style={{ color: 'var(--color-text-primary)', fontSize: '1.8rem', margin: 0 }}>Security Settings</h2>

      {isGoogleAccount ? (
        /* Google Account Notice */
        <div style={{
          background: 'rgba(59,130,246,0.08)',
          border: '1px solid rgba(59,130,246,0.15)',
          borderRadius: '12px',
          padding: '24px',
          display: 'flex',
          gap: '16px',
          alignItems: 'flex-start',
          marginTop: '10px'
        }}>
          <Info size={24} style={{ color: '#3b82f6', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <h4 style={{ color: 'var(--color-text-primary)', fontSize: '16px', margin: '0 0 6px 0', fontWeight: 600 }}>Google Sign-In Account</h4>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '13.5px', lineHeight: 1.5, margin: 0 }}>
              Password management is unavailable for Google-authenticated accounts. 
              Your authentication is managed securely by Google.
            </p>
          </div>
        </div>
      ) : (
        /* Standard Password Change Form */
        <form onSubmit={handlePasswordSubmit} style={{ marginTop: '10px' }}>
          
          {validationError && (
            <div style={{
              background: 'rgba(239,68,68,0.15)',
              color: '#fecaca',
              border: '1px solid rgba(248,113,113,0.3)',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {validationError}
            </div>
          )}

          {successMessage && (
            <div style={{
              background: 'rgba(34,197,94,0.15)',
              color: '#bef264',
              border: '1px solid rgba(134,239,172,0.3)',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {successMessage}
            </div>
          )}

          <div className="form-group-item">
            <label>Current Password *</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group-item">
            <label>New Password *</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min. 6 characters)"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group-item">
            <label>Confirm New Password *</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              disabled={loading}
            />
          </div>

          <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-start' }}>
            <button type="submit" className="btn" disabled={loading} style={{ padding: '12px 36px' }}>
              {loading ? 'Updating Password...' : 'Update Password'}
            </button>
          </div>

        </form>
      )}

    </div>
  );
};

export default SecurityTab;
